/**
 * E018-S005-T001: OnboardingView Component
 *
 * Displays a guided workflow selection UI for new automations.
 * Shows instead of the 3-tab interface when no workflow is selected.
 */

import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { ArrowLeftSmallIcon, PlayIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

import type { WorkflowInfo } from '../../services/workflow';
import { WorkflowService } from '../../services/workflow';
import * as styles from '../automation-panel.css';
import * as onboardingStyles from './onboarding-view.css';
import { WorkflowSchemaDisplay } from './workflow-schema';
import { WorkflowSelector } from './workflow-selector';

interface OnboardingViewProps {
  /** Callback to go back to overview */
  onBack: () => void;
  /** Shared active-automation title editor from the detail sidebar */
  titleEditor: ReactNode;
  /** Callback when workflow is confirmed */
  onConfirm: (workflow: WorkflowInfo) => void;
  /** DRY: Callback when workflow is selected (for immediate updates) */
  onChange?: (workflow: WorkflowInfo) => void;
}

export const OnboardingView: FC<OnboardingViewProps> = ({
  onBack,
  titleEditor,
  onConfirm,
  onChange,
}) => {
  const t = useI18n();
  const workflowService = useService(WorkflowService);
  const workflows = useLiveData(workflowService.executableWorkflows$);

  // Temporary state for workflow selection (not persisted until confirmed)
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );

  const selectedWorkflow = selectedWorkflowId
    ? (workflows.find((wf: WorkflowInfo) => wf.id === selectedWorkflowId) ??
      null)
    : null;

  const handleWorkflowSelect = (workflow: WorkflowInfo) => {
    setSelectedWorkflowId(workflow.id);
    // Load schema for preview
    workflowService.loadWorkflowSchema(workflow.id).catch(() => {});
    // DRY: Immediately update BlockModel (same as ProcessTab)
    onChange?.(workflow);
  };

  const handleConfirm = () => {
    if (selectedWorkflow) {
      onConfirm(selectedWorkflow);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={onboardingStyles.container}>
        {/* Header */}
        <div className={styles.header}>
          <button
            className={styles.backButton}
            onClick={onBack}
            title={t['com.streamlain.streamlain.common.back-to-overview']()}
          >
            <ArrowLeftSmallIcon width={20} height={20} />
          </button>
          {titleEditor}
        </div>

        {/* Onboarding Content */}
        <div className={onboardingStyles.content}>
          {/* Icon and Title */}
          <div className={onboardingStyles.hero}>
            <div className={onboardingStyles.iconWrapper}>
              <PlayIcon width={32} height={32} />
            </div>
            <h2 className={onboardingStyles.heroTitle}>
              {t['com.streamlain.streamlain.onboarding.configure-title']()}
            </h2>
            <p className={onboardingStyles.heroDescription}>
              {t[
                'com.streamlain.streamlain.onboarding.configure-description'
              ]()}
            </p>
          </div>

          {/* Workflow Selector */}
          <div className={onboardingStyles.selectorSection}>
            <WorkflowSelector
              value={selectedWorkflowId}
              onChange={handleWorkflowSelect}
              placeholder="Select a workflow..."
            />
          </div>

          {/* Schema Preview (only shown when workflow is selected) */}
          {selectedWorkflowId && (
            <div className={onboardingStyles.schemaSection}>
              <div className={styles.sectionTitle}>
                {t[
                  'com.streamlain.streamlain.onboarding.workflow-requirements'
                ]()}
              </div>
              <WorkflowSchemaDisplay workflowId={selectedWorkflowId} />
            </div>
          )}
        </div>

        {/* Confirm Button (sticky at bottom) */}
        <div className={onboardingStyles.footer}>
          <Button
            variant="primary"
            size="large"
            disabled={!selectedWorkflowId}
            onClick={handleConfirm}
            style={{ width: '100%' }}
          >
            {t['com.streamlain.streamlain.onboarding.select-workflow']()}
          </Button>
        </div>
      </div>
    </div>
  );
};
